import React, { useCallback, useEffect, useState } from 'react';
import {
  IPermissionAssignmentFieldsFragment,
  IPermissionFieldsFragment,
  IPermissionsQuery,
  IPermissionsQueryVariables,
  IRoleCreateMutation,
  IRoleCreateMutationVariables,
  IRoleEditMutation,
  IRoleEditMutationVariables,
  IRolePermissionAndAllPermissionsQuery,
  IRolePermissionAndAllPermissionsQueryVariables,
  IRoleWithPermissionsFieldsFragment,
  PermissionKey,
} from '../../../utils/apollo/graphqlSchema';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_PERMISSIONS,
  GET_ROLE_PERMISSION_AND_ALL_PERMISSIONS,
  ROLE_CREATE,
  ROLE_EDIT,
} from '../../../utils/apollo/permission';
import { useNavigate, useParams } from 'react-router-dom';
import { assertCondition, assertNotNil } from '../../../utils/types';
import { editInfoModalLookup } from '../../../components/EditInfoModal/utils';
import {
  ClickablePageTitle,
  PageHeaderDivider,
} from '../../../components/PageHeader';
import { appRoutes } from '../../../path';
import * as PermissionControlsUI from '../../PermissionControls/PermissionControlsUI';
import styled from 'styled-components';
import { DescriptionField, NameField } from '../../../components/Form';
import { z } from 'zod';
import {
  descriptionFieldSchema,
  nameFieldSchema,
} from '../../../utils/validation';
import PermissionSchemasSelector from '../../PermissionControls/PermissionSchemasSelector';
import PermissionRoleSelector from '../../PermissionControls/PermissionRoleSelector';
import ExpandableTable from '../../../components/ExpandableTable';
import usePermissionEditableList from '../../PermissionControls/hooks/usePermissionEditableList';
import { PrimaryButton, SecondaryButton } from '../../../components/Button';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { useBootstrapSelector } from '../../../redux/selectorHooks';
import usePermission from '../../../utils/hooks/permissionHooks/usePermission';
import PermissionElementWrapper from '../../PermissionControls/PermissionElementWrapper';
import { useImmer } from 'use-immer';
import { IBasicSchema } from '../../PermissionControls/types';
import toastMessage from '../../../utils/toastMessage';
import { ContentLoader } from '../../../components/Loaders';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import {
  roleCreated,
  roleUpdated,
} from '../../../utils/analytics/events/role/events';
import { formErrorViewed } from '../../../utils/analytics/events/error/events';
import { isNil } from 'lodash';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import { theme } from '../../../styles/theme';
import ThinDivider from '../../../components/ThinDivider';

export interface ICustomPermissionAssignmentInput {
  permissionKey: PermissionKey;
  schemas: IBasicSchema[];
  isActive: boolean;
  availableSchemas: IBasicSchema[];
}

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
  display: flex;
  column-gap: 8px;
  > span {
    color: ${theme.colors.secondary800};
    font-weight: 500;
  }
`;
const InputRowContainer = styled.div`
  align-self: flex-start;
  width: 600px;
`;
const TableHeaderRow = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.secondary900};
`;

const Footer = styled.div`
  display: flex;
  column-gap: 16px;
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 260px 200px;
  gap: 8px 4px;
`;
const GridContainerMenu = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 8px;
`;

const validationSchema = (nameMaxLimit: number) =>
  z.object({
    name: nameFieldSchema(nameMaxLimit),
    description: descriptionFieldSchema,
  });

/**
 * RoleModify is a page that allows user to create or edit a role.
 */
const RoleModify = () => {
  const { track } = useAnalytics();
  const { checkIsPermitted } = usePermission();
  const isPermittedToEdit = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminEdit],
  });
  const isPermittedToCreate = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AdminCreate],
  });

  const navigate = useNavigate();
  const permissionConfig = useBootstrapSelector('permissionConfig');
  assertNotNil(permissionConfig);
  const [permissionAssignmentsInput, setPermissionAssignmentsInput] = useImmer<
    ICustomPermissionAssignmentInput[]
  >([]);
  const [allPermissions, setAllPermissions] = useState<
    IPermissionFieldsFragment[]
  >([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultSchema, setDefaultSchema] = useState<IBasicSchema>(
    permissionConfig.allSchema,
  );
  const [submitting, setSubmitting] = useState(false);
  const urlParams = useParams();
  const roleId = urlParams.roleId;
  const isEditingRole = !isNil(roleId);

  useEffect(() => {
    track(
      pageViewed({
        category: 'settings',
        name: isEditingRole ? 'edit role' : 'create role',
        id: roleId,
      }),
    );
  }, [isEditingRole, roleId, track]);

  // fetch all permissions and role.permissions to generate permission list contains all permissions
  // with value in role.permission
  const getPermissionAssignmentsInput = useCallback(
    (
      allPermissions: IPermissionFieldsFragment[],
      permissionAssignments: IPermissionAssignmentFieldsFragment[],
    ): ICustomPermissionAssignmentInput[] =>
      allPermissions.map((permission) => {
        const permissionAssignment = permissionAssignments.find(
          (permissionAssignment) => {
            return permissionAssignment.permission.key === permission.key;
          },
        );
        if (!permissionAssignment) {
          return {
            permissionKey: permission.key,
            schemas: [permission.assignableSchemas[0]],
            isActive: false,
            availableSchemas: permission.assignableSchemas,
          };
        } else {
          return {
            permissionKey: permission.key,
            schemas: permissionAssignment.schemas,
            isActive: true,
            availableSchemas: permission.assignableSchemas,
          };
        }
      }),
    [],
  );

  const { editablePermissionTableList } = usePermissionEditableList({
    permissionInputs: permissionAssignmentsInput,
    setPermissionInputs: setPermissionAssignmentsInput,
  });

  // if editing role, fetch role info and all permissions for permission table
  const { loading: loadingSingleRole } = useQuery<
    IRolePermissionAndAllPermissionsQuery,
    IRolePermissionAndAllPermissionsQueryVariables
  >(GET_ROLE_PERMISSION_AND_ALL_PERMISSIONS, {
    variables: {
      id: roleId!,
      pagination: { first: 0, after: 0 },
    },
    skip: !isEditingRole,
    onCompleted: (data) => {
      const role = data.role;
      // update name and description of role
      setName(role.name);
      setDescription(role.description);

      const allPermissions = data.permissions.entities;
      setAllPermissions(allPermissions);
      if (allPermissions) {
        const permissionInputs = getPermissionAssignmentsInput(
          allPermissions,
          role.permissionAssignments,
        );
        setPermissionAssignmentsInput(permissionInputs);
      }
    },
  });

  // if create role, fetch all permissions for permission table
  const { loading: loadingPermissions } = useQuery<
    IPermissionsQuery,
    IPermissionsQueryVariables
  >(GET_PERMISSIONS, {
    variables: {
      // always get all permissions, no pagination here
      pagination: { first: 0, after: 0 },
    },
    onCompleted: (data) => {
      if (data.permissions.entities.length > 0) {
        setAllPermissions(data.permissions.entities);
        const permissionInputs = getPermissionAssignmentsInput(
          data.permissions.entities,
          [],
        );
        setPermissionAssignmentsInput(permissionInputs);
      }
    },
    skip: isEditingRole,
  });

  const [updateRole, { loading: isUpdateLoading }] = useMutation<
    IRoleEditMutation,
    IRoleEditMutationVariables
  >(ROLE_EDIT, {
    onCompleted: (data) => {
      const { entity, errors } = data.roleEdit;
      if (printGraphqlErrors(errors)) {
        return;
      }

      assertNotNil(entity);
      toastMessage({
        message: `${entity.name} has been updated.`,
        type: 'info',
      });
      track(roleUpdated(entity));
      navigate(appRoutes.permission.roles + `/${entity.id}`);
    },
  });

  const [createRole, { loading: isCreateLoading }] = useMutation<
    IRoleCreateMutation,
    IRoleCreateMutationVariables
  >(ROLE_CREATE, {
    onCompleted: (data) => {
      const { entity, errors } = data.roleCreate;
      if (printGraphqlErrors(errors)) {
        return;
      }

      assertNotNil(entity);
      toastMessage({
        message: `${entity.name} has been created.`,
        type: 'info',
      });
      track(roleCreated(entity));
      navigate(appRoutes.permission.roles + `/${entity.id}`);
    },
  });

  const modalConfig = isEditingRole
    ? editInfoModalLookup.editRole
    : editInfoModalLookup.createRole;

  const safeParse = validationSchema(modalConfig.nameLimit).safeParse({
    name,
    description,
  });
  const isFormValid = safeParse.success;
  const errors = !isFormValid ? safeParse?.error?.format() : null;

  const handleSubmit = async () => {
    setSubmitting(true);
    if (isFormValid) {
      const assignmentsInput = permissionAssignmentsInput
        .filter(
          (permissionInput) =>
            permissionInput.isActive && permissionInput.schemas.length > 0,
        )
        .map((permissionAssignment) => {
          return {
            permissionKey: permissionAssignment.permissionKey,
            schemaIds: permissionAssignment.schemas.map((schema) => schema.id),
          };
        });
      const { name, description } = safeParse.data;
      if (isEditingRole) {
        await updateRole({
          variables: {
            input: {
              roleId,
              name,
              description: description ?? '',
              permissionAssignments: assignmentsInput,
            },
          },
        });
      } else {
        await createRole({
          variables: {
            input: {
              name,
              description: description ?? '',
              permissionAssignments: assignmentsInput,
            },
          },
        });
      }
    } else {
      track(formErrorViewed(modalConfig.analyticsErrorProducer(errors)));
    }
  };

  // when select a starting role, update permissionAssignmentsInput for permission table
  const onSelectStartingRole = (
    role: IRoleWithPermissionsFieldsFragment | null,
  ) => {
    if (role) {
      const permissionAssignments = role.permissionAssignments;
      const permissionInputs = getPermissionAssignmentsInput(
        allPermissions,
        permissionAssignments,
      );
      setPermissionAssignmentsInput(permissionInputs);
    } else {
      const permissionInputs = getPermissionAssignmentsInput(
        allPermissions,
        [],
      );
      setPermissionAssignmentsInput(permissionInputs);
    }
  };

  const onDefaultSchemaChange = (schemas: IBasicSchema[]) => {
    assertCondition(schemas.length === 1);
    setDefaultSchema(schemas[0]);
    const newPermissionAssignmentsInput = permissionAssignmentsInput.map(
      (permissionAssignmentInput) => {
        const newSchemaIsAvailableInPermission =
          permissionAssignmentInput.availableSchemas.find(
            (availableSchema) => availableSchema.id === schemas[0].id,
          );
        return {
          ...permissionAssignmentInput,
          schemas: newSchemaIsAvailableInPermission
            ? schemas
            : permissionAssignmentInput.schemas,
        };
      },
    );

    setPermissionAssignmentsInput(newPermissionAssignmentsInput);
  };

  const getTitleList = () => {
    if (isEditingRole) {
      return [
        { name: 'Roles', path: appRoutes.permission.roles },
        {
          name: name ?? 'loading',
          path: appRoutes.permission.roles + `/${roleId}`,
        },
        { name: 'Edit', path: '' },
      ];
    } else {
      return [
        { name: 'Roles', path: appRoutes.permission.roles },
        { name: 'Create Role', path: '' },
      ];
    }
  };
  if (loadingSingleRole || loadingPermissions) {
    return <ContentLoader />;
  }

  return (
    <>
      <ClickablePageTitle titleList={getTitleList()} />
      <PageHeaderDivider />
      <PermissionControlsUI.OverviewContainer>
        <Title>
          {modalConfig.header}
          <span>[Unsaved]</span>
        </Title>
        <InputRowContainer>
          <NameField
            label={modalConfig.nameLabel}
            value={name}
            targetErrorElement={modalConfig.targetElement}
            maxLength={modalConfig.nameLimit}
            onChange={setName}
            hasError={submitting && Boolean(errors?.name)}
            noMargin={true}
          />
        </InputRowContainer>
        <InputRowContainer>
          <DescriptionField
            label={modalConfig.descriptionLabel}
            value={description}
            targetErrorElement={modalConfig.targetElement}
            onChange={setDescription}
            noMargin={true}
          />
        </InputRowContainer>

        {!isEditingRole && (
          <GridContainer>
            <span>Select a User Role as a starting point</span>
            <GridContainerMenu>
              <PermissionRoleSelector
                onSelectStartingRole={onSelectStartingRole}
              />
            </GridContainerMenu>
            <span>Select a default schema</span>
            <GridContainerMenu>
              <PermissionSchemasSelector
                setSchemas={onDefaultSchemaChange}
                schemaOptions={permissionConfig.schemas}
                selectedSchemas={[defaultSchema]}
                isMulti={false}
              />
            </GridContainerMenu>
          </GridContainer>
        )}
        <ThinDivider layout="horizontal" size="full" />
        <TableHeaderRow>Permissions</TableHeaderRow>
        <ExpandableTable tableList={editablePermissionTableList} />

        <Footer>
          <PermissionElementWrapper
            isPermitted={
              isEditingRole ? isPermittedToEdit : isPermittedToCreate
            }
          >
            <PrimaryButton
              onClick={handleSubmit}
              disabled={
                isEditingRole ? !isPermittedToEdit : !isPermittedToCreate
              }
              isGreyedOut={!isFormValid}
              loading={isCreateLoading || isUpdateLoading}
              trackContext="roles"
              trackAction={isEditingRole ? 'save' : 'create'}
            >
              {isEditingRole ? 'Save' : 'Create'}
            </PrimaryButton>
          </PermissionElementWrapper>
          <SecondaryButton
            onClick={() =>
              navigate(
                appRoutes.permission.roles + (roleId ? `/${roleId}` : ''),
              )
            }
            trackContext="roles"
            trackAction="cancel"
          >
            Cancel
          </SecondaryButton>
        </Footer>
      </PermissionControlsUI.OverviewContainer>
    </>
  );
};

export default RoleModify;
